import { useState } from "react"

import { ContentCopy, DeleteOutline, FileUpload } from "@mui/icons-material"
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material"

import { exportPaidJson, parsePaidJson, WEEKDAYS } from "../../entities/chats/api"
import { PaidPlaces as PaidPlacesMap, User } from "../../entities/chats/model"

import "./style.scss"

type Option = { uid: string; label: string; username?: string | null; search: string }

const toOption = (uid: string, users: User[]): Option => {
  const user = users.find((item) => item.uid === uid)
  if (!user) return { uid, label: `id ${uid}`, search: uid }
  const label = user.name || user.title || `id ${uid}`
  return { uid, label, username: user.username, search: `${user.name} ${user.title} ${user.username || ""} ${uid}` }
}
const filterOptions = createFilterOptions<Option>({ stringify: (option) => option.search })
const todayWeekday = () => String(((new Date().getDay() + 6) % 7) + 1)

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return
    } catch {
      // Telegram WebView может запретить Clipboard API — пробуем старый способ.
    }
  }
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.position = "fixed"
  textarea.style.opacity = "0"
  document.body.appendChild(textarea)
  textarea.select()
  const copied = document.execCommand("copy")
  document.body.removeChild(textarea)
  if (!copied) throw new Error("copy failed")
}

type Props = {
  paid: PaidPlacesMap
  users: User[]
  onChange: (weekday: string, uids: string[]) => void
}

const PaidPlaces = ({ paid, users, onChange }: Props) => {
  const [weekday, setWeekday] = useState(
    () => WEEKDAYS.map((_, index) => String(index + 1)).find((day) => paid[day]?.length) || todayWeekday(),
  )
  const [notice, setNotice] = useState<{ severity: "success" | "error"; text: string } | null>(null)
  const [importOpen, setImportOpen] = useState(false)
  const [importText, setImportText] = useState("")
  const [importError, setImportError] = useState("")
  const [clearOpen, setClearOpen] = useState(false)

  const uids = paid[weekday] || []
  const options = users.filter((user) => user.uid).map((user) => toOption(user.uid as string, users))
  const dayName = WEEKDAYS[Number(weekday) - 1]

  const copy = () => {
    copyText(exportPaidJson(uids, users))
      .then(() => setNotice({ severity: "success", text: `Список (${dayName}) скопирован в буфер обмена` }))
      .catch(() => setNotice({ severity: "error", text: "Не удалось скопировать. Разрешите доступ к буферу обмена." }))
  }
  const confirmClear = () => {
    onChange(weekday, [])
    setClearOpen(false)
    setNotice({ severity: "success", text: `Список (${dayName}) очищен` })
  }
  const submitImport = () => {
    try {
      const imported = parsePaidJson(importText)
      onChange(weekday, imported)
      setImportOpen(false)
      setImportText("")
      setImportError("")
      setNotice({ severity: "success", text: `Импортировано участников: ${imported.length} (${dayName})` })
    } catch (reason) {
      setImportError(reason instanceof Error ? reason.message : "Не удалось импортировать список.")
    }
  }

  return (
    <section className="paid-places" aria-label="Платные места">
      <div className="paid-heading">
        <span className="rank-label">💎 ПЛАТНЫЕ МЕСТА</span>
        <p>Участники с оплатой на выбранный день попадают в состав первыми.</p>
      </div>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={weekday}
        onChange={(_, next) => next && setWeekday(next)}
        aria-label="День недели"
        className="paid-weekdays"
      >
        {WEEKDAYS.map((label, index) => {
          const day = String(index + 1)
          const count = paid[day]?.length || 0
          return (
            <ToggleButton key={day} value={day} aria-label={label}>
              {label}
              {count > 0 && <span className="paid-count">{count}</span>}
            </ToggleButton>
          )
        })}
      </ToggleButtonGroup>
      <Autocomplete
        multiple
        disableCloseOnSelect
        filterSelectedOptions
        options={options}
        value={uids.map((uid) => toOption(uid, users))}
        filterOptions={filterOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.uid === value.uid}
        onChange={(_, value) =>
          onChange(
            weekday,
            value.map((option) => option.uid),
          )
        }
        noOptionsText="Никого не нашли"
        renderOption={(props, option) => (
          <li {...props} key={option.uid}>
            <span className="paid-option">
              {option.label}
              {option.username && <span className="paid-username">@{option.username}</span>}
            </span>
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip {...getTagProps({ index })} key={option.uid} size="small" label={`💎 ${option.label}`} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={`Платные участники: ${dayName}`} placeholder="Имя или ник" />
        )}
      />
      <div className="paid-actions">
        <Button size="small" startIcon={<ContentCopy />} onClick={copy} disabled={!uids.length}>
          Скопировать JSON
        </Button>
        <Button size="small" startIcon={<FileUpload />} onClick={() => setImportOpen(true)}>
          Импорт JSON
        </Button>
        <Button
          size="small"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={() => setClearOpen(true)}
          disabled={!uids.length}
        >
          Очистить
        </Button>
      </div>
      {notice && (
        <Alert severity={notice.severity} className="paid-notice" onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}
      <Dialog open={clearOpen} onClose={() => setClearOpen(false)} fullWidth>
        <DialogTitle>Очистить список: {dayName}?</DialogTitle>
        <DialogContent>
          <p className="paid-import-hint">
            Из платных мест на этот день будут удалены все участники ({uids.length}). Действие нельзя отменить — при
            необходимости сначала скопируйте JSON.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearOpen(false)}>Отмена</Button>
          <Button variant="contained" color="error" onClick={confirmClear}>
            Очистить
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth>
        <DialogTitle>Импорт списка: {dayName}</DialogTitle>
        <DialogContent>
          <p className="paid-import-hint">
            Вставьте JSON-массив uid или объектов с полем uid. Текущий список на этот день будет заменён.
          </p>
          <TextField
            label="JSON"
            multiline
            minRows={6}
            fullWidth
            autoFocus
            value={importText}
            onChange={(event) => {
              setImportText(event.target.value)
              setImportError("")
            }}
            error={Boolean(importError)}
            helperText={importError || '[{"uid": "123456"}, "789012"]'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={submitImport} disabled={!importText.trim()}>
            Импортировать
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  )
}
export default PaidPlaces
